import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { useAppSelector, useMobile } from '@pkg/hooks';

import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import config from '@app/config';
import { PathParams } from '@app/routes/types';
import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

import { YatMagnetism } from '../Animations';
import { Typography } from '../DesignSystem';
import CenteredContentSpaceBetween from '../Layout/CenteredContentSpaceBetween';
import { LogoLink } from '../LogoLink';
import { YatModalBottomSheet } from '../ModalBottomSheet';
import { YatModalSideSheet } from '../ModalSideSheet';
import { RhythmScoreModal } from '../RhythmScoreModal';
import PickerResult from '../YatPicker/PickerResult';
import { PickerResultRhythmScore } from '../YatPicker/PickerResult/PickerResultRhythmScore';

import './404Overlay.scss';
import { NotFoundYatList } from './YatList';

interface Props {
    emojiId?: EmojiId;
}
export const EmojiIDNotFoundOverlay = ({ emojiId }: Props) => {
    const { [PathParams.Yat]: yat } = useParams();
    const { t } = useTranslation(['notFound']);
    const rhythmScore = useAppSelector((r) => r.picker.rhythmScore);
    const rhythmScoreCopy = useAppSelector((r) => r.picker.rhythmScoreCopy);
    const isAuthenticated = useAuthTokens((state) => state.isAuthenticated);

    const [RSInfoModalOpen, setRSInfoModalOpen] = useState(false);
    const navigate = useNavigate();
    const { isMobile } = useMobile();

    const handleGoCreate = useCallback(() => {
        navigate('/create', { state: { enableGoBack: true } });
    }, [navigate]);

    const onClose = useCallback(() => {
        if (isAuthenticated) {
            navigate(config.homeUrl);
        } else {
            window.location.href = config.landingPageUrl;
        }
    }, [isAuthenticated, navigate]);

    if (!emojiId && !yat) {
        return <Navigate to={'/'} />;
    }
    return (
        <div className="not-found-overlay v2-global-reset">
            <CenteredContentSpaceBetween transparentBackground>
                <LogoLink classes="not-found-overlay__logo" />
                <YatMagnetism classes="not-found-overlay__close">
                    <div className={`yat-circle-button ${!isMobile && 'yat-circle-button--large'}`} onClick={onClose}>
                        <CloseIcon />
                    </div>
                </YatMagnetism>
                <div className="not-found-overlay__content">
                    <Typography type="headline" size={40} classes={['not-found-overlay__title']} variant="h2">
                        {t('404_still_available')}
                    </Typography>
                    <NotFoundYatList emojiId={emojiId ?? yat} />
                    <div className="not-found-overlay__actions">
                        <PickerResult preEmojiId={emojiId ?? yat} walletKey="" />
                        <div className="not-found-overlay__scores">
                            <PickerResultRhythmScore
                                showScore={true}
                                score={rhythmScore}
                                onRhythmScoreClick={() => {
                                    setRSInfoModalOpen(true);
                                }}
                                preYat
                            />
                        </div>
                    </div>
                    <div onClick={handleGoCreate}>
                        <Typography type="headline" classes={['not-found-overlay__copy']}>
                            {t('404_create_different')}
                        </Typography>
                    </div>
                    <div className="not-found-overlay__modal">
                        {isMobile ? (
                            <YatModalBottomSheet
                                open={RSInfoModalOpen}
                                modalLabelId="yat-picker-cart-title"
                                closeCallback={() => setRSInfoModalOpen(false)}
                            >
                                <RhythmScoreModal rhythmScore={rhythmScore} rhythmScoreCopy={rhythmScoreCopy} />
                            </YatModalBottomSheet>
                        ) : (
                            <YatModalSideSheet
                                open={RSInfoModalOpen}
                                side="right"
                                modalLabelId="yat-picker-rs-modal-title"
                                closeCallback={() => setRSInfoModalOpen(false)}
                            >
                                <RhythmScoreModal rhythmScore={rhythmScore} rhythmScoreCopy={rhythmScoreCopy} />
                            </YatModalSideSheet>
                        )}
                    </div>
                </div>
            </CenteredContentSpaceBetween>
        </div>
    );
};
